import React from "react";
import PropTypes from "prop-types";

import ActionButton from "../formInputs/buttons/ActionButton";
import SimpleAutoSuggest from "../formInputs/SimpleAutoSuggest";

import "../../styles/NewShareForm.scss";

/**
 * @component NewShareForm
 * @description Form component for initiating a new grading profile share with a colleague.
 *
 * @param {Object[]} autoSuggest
 * @param {Object[]} existingShares
 * @param {Object} formState
 * @param {Function} handleChange
 * @param {Function} handleClearSearch
 * @param {Object|null} selectedEmployee
 * @param {Function} setExistingShares
 * @param {Function} setOpenAccordionKey
 * @param {Function} setSelectedEmployee
 * @param {Function} setShow
 * @param {Function} setShowAccordion
 * @param {Function} [setShowShareModal]
 * @param {boolean} show
 * @param {boolean} showAccordion
 * @param {boolean} [isModal=false]
 */
const NewShareForm = ({
    autoSuggest,
    existingShares,
    formState,
    handleChange,
    handleClearSearch,
    selectedEmployee,
    setExistingShares,
    setOpenAccordionKey,
    setSelectedEmployee,
    setShow,
    setShowAccordion,
    setShowShareModal,
    show,
    showAccordion,
    isModal = false
}) => {
    return (
        <section className="form-wrapper">
            {!isModal && (
                <>
                    <div className="employee-search-title">Colleague to Share with:</div>
                    <hr />
                </>
            )}
            <div className="employee-section">
                <SimpleAutoSuggest
                    formState={formState}
                    handleChange={handleChange}
                    handleOptionClick={(employee) => {
                        setSelectedEmployee(employee);
                        setShowAccordion(false);
                    }}
                    name="groupShare"
                    noneFoundMessage="No employees found, please enter a valid name, username, or employee number."
                    options={autoSuggest}
                    searchLabel="Search Employee By Name, Username, or School:"
                    setShow={setShow}
                    show={show}
                />
                <div className="clear-button-wrapper">
                    <ActionButton className="action-button-reg" label="Clear" onClick={handleClearSearch} />
                </div>
            </div>

            {selectedEmployee && (
                <section className="selected-employee-section">
                    <div className="selected-employee-box">
                        <div className="selected-employee-info">
                            <div className="selected-employee-header">
                                {selectedEmployee.givenName} {selectedEmployee.lastName}
                            </div>
                            <div>
                                {selectedEmployee.school} - {selectedEmployee.emailAddress}
                            </div>
                        </div>
                        {!showAccordion && (
                            <div className="selected-employee-button">
                                <ActionButton
                                    className="action-button-reg"
                                    label="Start Share"
                                    onClick={() => {
                                        const existing = existingShares.find(
                                            (s) => parseInt(s.guid, 10) === parseInt(selectedEmployee.guid, 10)
                                        );

                                        if (existing) {
                                            setOpenAccordionKey(existing.key.toString());
                                        } else {
                                            setShowAccordion(true);
                                            const tempShare = {
                                                key: `temp-${selectedEmployee.guid}`,
                                                guid: selectedEmployee.guid,
                                                displayName: `${selectedEmployee.lastName}, ${selectedEmployee.givenName}`,
                                                gradingCriteriaShares: [],
                                                isNew: true
                                            };
                                            setExistingShares((prev) => [
                                                tempShare,
                                                ...prev.filter((s) => s.guid !== tempShare.guid)
                                            ]);
                                            setOpenAccordionKey(tempShare.key);
                                            handleClearSearch();
                                            setShowShareModal ? setShowShareModal(false) : null;
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </section>
            )}
        </section>
    );
};

NewShareForm.propTypes = {
    autoSuggest: PropTypes.arrayOf(PropTypes.object),
    existingShares: PropTypes.arrayOf(
        PropTypes.shape({
            key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            guid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            displayName: PropTypes.string,
            gradingCriteriaShares: PropTypes.array,
            isNew: PropTypes.bool
        })
    ).isRequired,
    formState: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    handleClearSearch: PropTypes.func.isRequired,
    selectedEmployee: PropTypes.shape({
        givenName: PropTypes.string,
        lastName: PropTypes.string,
        guid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        school: PropTypes.string,
        emailAddress: PropTypes.string
    }),
    setExistingShares: PropTypes.func.isRequired,
    setOpenAccordionKey: PropTypes.func.isRequired,
    setSelectedEmployee: PropTypes.func.isRequired,
    setShow: PropTypes.func.isRequired,
    setShowAccordion: PropTypes.func.isRequired,
    setShowShareModal: PropTypes.func,
    show: PropTypes.bool.isRequired,
    showAccordion: PropTypes.bool.isRequired,
    isModal: PropTypes.bool
};

NewShareForm.defaultProps = {
    autoSuggest: [],
    selectedEmployee: null,
    setShowShareModal: null,
    isModal: false
};

export default NewShareForm;
